import * as React from 'react'

import {
  CompanyDocumentsRouteDocument,
  CompanyDocumentsRouteQuery,
  DocumentTemplateHeaderDocument,
  InternalFilesFolderPageHeaderDocument,
  PublicFilesFolderPageHeaderDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import NewCompanyDocumentPage from 'modules/documents/pages/NewCompanyFiles/NewDocumentPage'

const RedirectToDocumentUrl = React.lazy(
  () => import('modules/documents/pages/RedirectToDocumentUrl')
)

const BulkFilesPage = React.lazy(() => import('modules/documents/pages/BulkFilesPage'))
const DocumentTemplateDetailsPage = React.lazy(
  () => import('modules/documents/pages/DocumentTemplates/DetailsPage')
)
const DocumentTemplateDetailsPageHeader = React.lazy(
  () => import('modules/documents/pages/DocumentTemplates/DetailsPage/DetailsPageHeader')
)
const DocumentTemplatesPage = React.lazy(
  () => import('modules/documents/pages/DocumentTemplates/ListPage')
)
const VariableTemplatesPage = React.lazy(
  () => import('modules/documents/pages/DocumentTemplates/VariableTemplatesPage')
)
const ListEmployeeFilesPage = React.lazy(
  () => import('modules/documents/pages/EmployeeFiles/ListPage')
)
const DocumentViewerPage = React.lazy(() => import('modules/documents/pages/DocumentViewerPage'))
const InternalCompanyDocumentsPage = React.lazy(
  () => import('modules/documents/pages/InternalCompanyFiles/DocumentsPage')
)
const InternalCompanyFolderDocumentsPage = React.lazy(
  () => import('modules/documents/pages/InternalCompanyFiles/FolderDocumentsPage')
)
const InternalCompanyFolderDocumentsPageHeader = React.lazy(
  () =>
    import(
      'modules/documents/pages/InternalCompanyFiles/FolderDocumentsPage/FolderDocumentsPageHeader'
    )
)
const PublicCompanyDocumentsPage = React.lazy(
  () => import('modules/documents/pages/PublicCompanyFiles/DocumentsPage')
)
const PublicCompanyFolderDocumentsPage = React.lazy(
  () => import('modules/documents/pages/PublicCompanyFiles/FolderDocumentsPage')
)
const PublicCompanyFolderDocumentsPageHeader = React.lazy(
  () =>
    import(
      'modules/documents/pages/PublicCompanyFiles/FolderDocumentsPage/FolderDocumentsPageHeader'
    )
)
const RedirectToCompanyFilesListPage = React.lazy(
  () => import('modules/documents/pages/RedirectToCompanyFilesListPage')
)
const RedirectToEmployeeFilesListPage = React.lazy(
  () => import('modules/documents/pages/RedirectToEmployeeFilesListPage')
)
const RedirectToInternalCompanyDocumentsListPage = React.lazy(
  () => import('modules/documents/pages/RedirectToInternalCompanyDocumentsListPage')
)
const RedirectToFirstAvailableFilesTab = React.lazy(
  () => import('modules/documents/pages/RedirectToFirstAvailableFilesTab')
)
const RootPage = React.lazy(() => import('modules/documents/pages/RootPage'))
const CompanyInternalTrashPage = React.lazy(
  () => import('modules/documents/pages/Trash/CompanyInternalTrashPage')
)
const CompanyPublicTrashPage = React.lazy(
  () => import('modules/documents/pages/Trash/CompanyPublicTrashPage')
)
const EmployeeTrashPage = React.lazy(
  () => import('modules/documents/pages/Trash/EmployeeTrashPage')
)
const TrashRootPage = React.lazy(() => import('modules/documents/pages/Trash/TrashRootPage'))

export const canSeePublicFilesTab = (data: CompanyDocumentsRouteQuery) =>
  data?.documents?._policies?.companyUseCompanyDocuments.can ||
  data?.documents?._policies?.companySeePublicDocuments.can

export const canSeeInternalFilesTab = (data: CompanyDocumentsRouteQuery) =>
  data?.documents?._policies?.companyUseCompanyDocuments.can ||
  data?.documents?._policies?.companySeeInternalDocuments.can

export const canSeeEmployeeFilesTab = (data: CompanyDocumentsRouteQuery) =>
  data?.documents?._policies?.companyUseCompanyDocuments.can ||
  data?.documents?._policies?.employeeManageAnyEmployeeFolder.can ||
  data?.documents?._policies?.employeeSeeEmployeeFiles.can

export const canSeeSendInBulkTab = (data: CompanyDocumentsRouteQuery) =>
  data?.documents?._policies?.companyUseCompanyDocuments.can ||
  data?.documents?._policies?.employeeManageAnyEmployeeFolder.can

export const canSeeTemplatesTab = (data: CompanyDocumentsRouteQuery) =>
  data?.documents?._policies?.companyUseCompanyDocuments.can ||
  data?.documents?._policies?.templateManageDocumentTemplates.can

const canSeeEmployeeTab = (data: CompanyDocumentsRouteQuery) =>
  data?.documents?._policies?.companyUseCompanyDocuments.can ||
  data?.documents?._policies?.employeeEditDocumentsTrashBin.can ||
  data?.documents?._policies?.employeeManageAnyEmployeeFolder.can || // TODO deprecate when removing dev_document_folder_permissions
  data?.documents?._policies?.employeeManageDefaultEmployeeFolder.can || // TODO deprecate when removing dev_document_folder_permissions
  data?.documents?._policies?.employeeEditAnyEmployeeFolder.can

const canSeePublicTab = (data: CompanyDocumentsRouteQuery) =>
  data?.documents?._policies?.companyUseCompanyDocuments.can ||
  data?.documents?._policies?.companyEditPublicDocuments.can

const canSeeInternalTab = (data: CompanyDocumentsRouteQuery) =>
  data?.documents?._policies?.companyUseCompanyDocuments.can ||
  data?.documents?._policies?.companyEditInternalDocuments.can

export const canSeeTrashTab = (data: CompanyDocumentsRouteQuery) =>
  canSeeEmployeeTab(data) || canSeePublicTab(data) || canSeeInternalTab(data)

const templatesSubTabs = (navigation: Navigation<NavigationPaths>) => {
  return [
    {
      title: i18n.t('documents.document_templates.list.tabs.templates'),
      href: navigation.files.documentTemplates.fullPath(),
      index: true,
    },
    {
      title: i18n.t('documents.document_templates.list.tabs.variables'),
      href: navigation.files.templateVariables.fullPath(),
    },
  ]
}

const buildTabName = (title: string, counter?: number | null) =>
  counter ? `${title} (${counter})` : title

const trashSubTabs = (
  data: CompanyDocumentsRouteQuery,
  navigation: Navigation<NavigationPaths>
) => {
  const employeeCounter = data?.documents.employeeDocuments?.totalCount || 0
  const companyPublicCounter = data?.documents.companyPublicDocuments?.totalCount || 0
  const companyInternalCounter = data?.documents.companyInternalDocuments?.totalCount || 0

  return [
    {
      title: buildTabName(i18n.t('documents.trash.sections.employee'), employeeCounter),
      href: navigation.files.trash.employee.fullPath(),
      isVisible: canSeeEmployeeTab(data),
      index: true,
    },
    {
      title: buildTabName(i18n.t('documents.trash.sections.company_public'), companyPublicCounter),
      href: navigation.files.trash.company_public.fullPath(),
      isVisible: canSeePublicTab(data),
    },
    {
      title: buildTabName(
        i18n.t('documents.trash.sections.company_internal'),
        companyInternalCounter
      ),
      href: navigation.files.trash.company_internal.fullPath(),
      isVisible: canSeeInternalTab(data),
    },
  ]
}

export const filesRoutes: NavigationRoutesFn = (navigation) => [
  {
    path: navigation.files.path,
    element: <RootPage />,
    handle: {
      module: 'documents',
      meta: ({ watchQuery }) => {
        return watchQuery({
          query: CompanyDocumentsRouteDocument,
          variables: { byTrashBin: true },
        }).map(({ data }) => {
          const editSettingsPublicDocuments =
            data?.documents?._policies.companyManagePublicCompanyFolders.can ||
            data?.documents?._policies.companyUseCompanyDocuments.can
          return {
            title: i18n.t('company_settings.general.documents.title'),
            settings: editSettingsPublicDocuments
              ? navigation.settings.folders.public.fullPath()
              : undefined,
            subNavigation: [
              {
                title: i18n.t('sidebar.public'),
                href: navigation.files.companyFiles.list.fullPath(),
                isVisible: canSeePublicFilesTab(data),
                index: true,
              },
              {
                title: i18n.t('sidebar.management_documents'),
                href: navigation.files.managementDocuments.fullPath(),
                isVisible: canSeeInternalFilesTab(data),
              },
              {
                title: i18n.t('documents.employee.section_title'),
                href: navigation.files.employeeFiles.fullPath(),
                isVisible: canSeeEmployeeFilesTab(data),
              },
              {
                title: i18n.t('split_documents.summary.title'),
                href: navigation.files.bulkFiles.fullPath(),
                isVisible: canSeeSendInBulkTab(data),
              },
              {
                title: i18n.t('documents.templates.navigation_title'),
                href: navigation.files.documentTemplates.fullPath(),
                isVisible: canSeeTemplatesTab(data),
              },
              {
                title: i18n.t('documents.trash.title'),
                href: navigation.files.trash.fullPath(),
                isVisible: canSeeTrashTab(data),
              },
            ],
          }
        })
      },
    },
    children: [
      {
        index: true,
        element: <RedirectToFirstAvailableFilesTab />,
      },
      {
        path: navigation.files.bulkFiles.path,
        element: <BulkFilesPage />,
        handle: {
          meta: () => ({
            title: i18n.t('split_documents.summary.title'),
          }),
        },
      },
      {
        path: navigation.files.documentTemplates.path,
        handle: {
          meta: () => ({
            title: i18n.t('documents.templates.navigation_title'),
            subNavigation: templatesSubTabs(navigation),
          }),
        },
        children: [
          {
            index: true,
            element: <DocumentTemplatesPage />,
          },
          {
            path: navigation.files.documentTemplates.documentTemplateId.path,
            element: <DocumentTemplateDetailsPage />,
            handle: {
              resource: true,
              meta: ({ params, watchQuery }) => {
                if (!params['documentTemplateId']) return { title: '' }
                return watchQuery({
                  query: DocumentTemplateHeaderDocument,
                  variables: {
                    templateId: Number(params['documentTemplateId']),
                  },
                }).map(({ data }) => {
                  const documentTemplate = data?.documents?.template

                  return {
                    title: documentTemplate?.name,
                    headerComponent: (
                      <DocumentTemplateDetailsPageHeader documentTemplate={documentTemplate} />
                    ),
                  }
                })
              },
            },
          },
        ],
      },
      {
        path: navigation.files.templateVariables.path,
        element: <VariableTemplatesPage />,
        handle: {
          meta: () => ({
            title: i18n.t('documents.templates.navigation_title'),
            subNavigation: templatesSubTabs(navigation),
          }),
        },
      },
      {
        path: navigation.files.employeeFiles.path,
        children: [
          {
            index: true,
            element: <RedirectToEmployeeFilesListPage />,
          },
          {
            path: navigation.files.employeeFiles.list.path,
            element: <ListEmployeeFilesPage />,
            handle: {
              meta: () => ({
                title: i18n.t('documents.employee.section_title'),
              }),
            },
            children: [
              {
                path: navigation.files.employeeFiles.list.documentId.path,
                children: [
                  {
                    index: true,
                    element: <DocumentViewerPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: navigation.files.companyFiles.path,
        handle: {
          meta: () => ({
            title: i18n.t('sidebar.public'),
          }),
        },
        children: [
          {
            index: true,
            element: <RedirectToCompanyFilesListPage />,
          },
          {
            path: navigation.files.companyFiles.list.path,
            element: <PublicCompanyDocumentsPage />,
            children: [
              {
                path: navigation.files.companyFiles.list.new.path,
                element: <NewCompanyDocumentPage />,
              },
              {
                path: navigation.files.companyFiles.list.documentId.path,
                element: <DocumentViewerPage />,
              },
            ],
          },
          {
            path: navigation.files.companyFiles.folderId.path,
            element: <PublicCompanyFolderDocumentsPage />,
            handle: {
              resource: true,
              meta: ({ params, watchQuery }) => {
                if (!params['folderId']) return { title: '' }
                return watchQuery({
                  query: PublicFilesFolderPageHeaderDocument,
                  variables: {
                    folderId: Number(params['folderId']),
                  },
                }).map(({ data }) => {
                  const folder = data?.documents?.foldersConnection?.nodes[0]
                  const title = folder ? folder.name : ''

                  const ancestors = data?.documents?.ancestors?.nodes?.map((node) => ({
                    id: node.id,
                    title: node.name,
                    to: navigation.files.companyFiles.folderId.fullPath({ folderId: node.id }),
                  }))

                  return {
                    title,
                    ancestors,
                    headerComponent: folder ? (
                      <PublicCompanyFolderDocumentsPageHeader
                        policies={data?.documents?._policies}
                        folder={folder}
                      />
                    ) : null,
                  }
                })
              },
            },
            children: [
              {
                path: navigation.files.companyFiles.folderId.new.path,
                element: <NewCompanyDocumentPage />,
              },
              {
                path: navigation.files.companyFiles.folderId.documentId.path,
                element: <DocumentViewerPage />,
              },
            ],
          },
        ],
      },
      {
        path: navigation.files.managementDocuments.path,
        handle: {
          meta: () => ({
            title: i18n.t('sidebar.management_documents'),
          }),
        },
        children: [
          {
            index: true,
            element: <RedirectToInternalCompanyDocumentsListPage />,
          },
          {
            path: navigation.files.managementDocuments.list.path,
            element: <InternalCompanyDocumentsPage />,
            children: [
              {
                path: navigation.files.managementDocuments.list.new.path,
                element: <NewCompanyDocumentPage />,
              },
              {
                path: navigation.files.managementDocuments.list.documentId.path,
                element: <DocumentViewerPage />,
              },
            ],
          },
          {
            path: navigation.files.managementDocuments.folderId.path,
            element: <InternalCompanyFolderDocumentsPage />,
            handle: {
              resource: true,
              meta: ({ params, watchQuery }) => {
                if (!params['folderId']) return { title: '' }
                return watchQuery({
                  query: InternalFilesFolderPageHeaderDocument,
                  variables: {
                    folderId: Number(params['folderId']),
                  },
                }).map(({ data }) => {
                  const folder = data?.documents?.foldersConnection?.nodes[0]
                  const title = folder ? folder.name : ''

                  const ancestors = data?.documents?.ancestors?.nodes?.map((node) => ({
                    id: node.id,
                    title: node.name,
                    to: navigation.files.managementDocuments.folderId.fullPath({
                      folderId: node.id,
                    }),
                  }))
                  return {
                    title,
                    ancestors,
                    headerComponent: folder ? (
                      <InternalCompanyFolderDocumentsPageHeader
                        policies={data?.documents?._policies}
                        folder={folder}
                      />
                    ) : null,
                  }
                })
              },
            },
            children: [
              {
                path: navigation.files.managementDocuments.folderId.new.path,
                element: <NewCompanyDocumentPage />,
              },
              {
                path: navigation.files.managementDocuments.folderId.documentId.path,
                element: <DocumentViewerPage />,
              },
            ],
          },
        ],
      },
      {
        path: navigation.files.trash.path,
        element: <TrashRootPage />,
        handle: {
          meta: ({ watchQuery }) => {
            return watchQuery({
              query: CompanyDocumentsRouteDocument,
              variables: { byTrashBin: true },
            }).map(({ data }) => {
              return {
                title: i18n.t('documents.trash.title'),
                subNavigation: trashSubTabs(data, navigation),
              }
            })
          },
        },
        children: [
          {
            path: navigation.files.trash.employee.path,
            element: <EmployeeTrashPage />,
            children: [
              {
                path: navigation.files.trash.employee.documentId.path,
                children: [
                  {
                    index: true,
                    element: <DocumentViewerPage />,
                  },
                ],
              },
            ],
          },
          {
            path: navigation.files.trash.company_public.path,
            element: <CompanyPublicTrashPage />,
            children: [
              {
                path: navigation.files.trash.company_public.documentId.path,
                children: [
                  {
                    index: true,
                    element: <DocumentViewerPage />,
                  },
                ],
              },
            ],
          },
          {
            path: navigation.files.trash.company_internal.path,
            element: <CompanyInternalTrashPage />,
            children: [
              {
                path: navigation.files.trash.company_internal.documentId.path,
                children: [
                  {
                    index: true,
                    element: <DocumentViewerPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: navigation.files.notifierDocuments.path,
        element: <RedirectToDocumentUrl />,
      },
    ],
  },
]
